/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import mapboxgl, { type Map, type MapboxOptions } from 'mapbox-gl';

import {
    MapContext,
    type RecenterOptions,
    type MapContextValue,
    type MapErrorType,
} from './MapContext';

export const DEFAULT_CENTER: [number, number] = [-98.35, 39.5]; // Lebanon, Kansas
const DEFAULT_ZOOM = 4;

const noopRecenter = () => {};

export interface MapProviderProps {
    children: React.ReactNode;
    /** `[longitude, latitude]`. Falls back to Washington, D.C. when omitted. */
    initialCenter?: [number, number];
    zoom?: number;
    /**
     * Mapbox constructor options. Merged over the internally-provided
     * `container`, `style`, `center`, and `zoom` — those cannot be overridden
     * because the provider owns them.
     */
    mapOptions?: Omit<MapboxOptions, 'container' | 'style' | 'center' | 'zoom'>;
}

/**
 * Owns a Mapbox GL map instance and exposes it via context.
 *
 * Consumers attach `mapContainerRef` to a sized DOM element (typically inside
 * `children`) and read `mapInstance` from `useMap()` once it becomes non-null.
 * The instance is withheld until the map's style has fully loaded so hooks
 * that add sources/layers do not race the initial `styledata` event.
 */
export function MapProvider({
    children,
    initialCenter,
    zoom = DEFAULT_ZOOM,
    mapOptions,
}: MapProviderProps) {
    const [mapInstance, setMapInstance] = React.useState<Map | null>(null);
    const [mapErrorType, setMapErrorType] = React.useState<MapErrorType | null>(null);

    const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
    const hasMapLoadedRef = React.useRef(false);
    const wasMapRemovedByErrorRef = React.useRef(false);
    const recenterRef = React.useRef<(options?: RecenterOptions) => void>(noopRecenter);

    React.useEffect(() => {
        if (hasMapLoadedRef.current) return;
        if (!mapContainerRef.current) return;
        if (!mapboxgl.supported()) {
            setMapErrorType('webgl');
            return;
        }

        mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN as string;

        let map: Map;
        try {
            map = new mapboxgl.Map({
                // Default hide mapbox and open street map attribution links; override via mapOptions.attributionControl
                attributionControl: false,
                ...mapOptions,
                container: mapContainerRef.current,
                style: process.env.MAPBOX_STYLE as string,
                center: initialCenter ?? DEFAULT_CENTER,
                zoom,
            });
        } catch {
            setMapErrorType('webgl');
            return;
        }

        const onError = () => {
            if (wasMapRemovedByErrorRef.current) return;
            wasMapRemovedByErrorRef.current = true;
            map.off('error', onError);
            map.remove();
            setMapInstance(null);
            setMapErrorType('map_error');
        };

        map.on('error', onError);

        const removeErrorListener = () => {
            map.off('error', onError);
        };

        const publish = () => {
            removeErrorListener();
            hasMapLoadedRef.current = true;
            setMapInstance(map);
        };

        map.on('load', () => {
            // Gate on styledata: sources/layers added before the style is
            // fully loaded race the initial style parse and can vanish.
            if (!map.isStyleLoaded()) {
                map.once('styledata', publish);
            } else {
                publish();
            }
        });

        return () => {
            setMapInstance(null);
            hasMapLoadedRef.current = false;
            if (!wasMapRemovedByErrorRef.current) {
                map?.remove();
            }
        };
        // Intentionally empty: create once on mount, destroy on unmount.
        // Re-running on prop changes would tear down the map mid-session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mapbox only tracks window resizes, so a container that changes size on its
    // own (e.g. an expand toggle) would leave the canvas stretched or clipped.
    // `resize()` returns early when the size hasn't changed, so window resizes
    // that both this and Mapbox see only redraw once.
    React.useEffect(() => {
        const container = mapContainerRef.current;
        if (!mapInstance || !container) return;

        const observer = new ResizeObserver(() => {
            mapInstance.resize();
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, [mapInstance]);

    const registerRecenter = React.useCallback(
        (fn: (options?: RecenterOptions) => void) => {
            recenterRef.current = fn;
        },
        []
    );

    const recenter = React.useCallback((options?: RecenterOptions) => {
        recenterRef.current(options);
    }, []);

    const value = React.useMemo<MapContextValue>(
        () => ({
            mapInstance,
            mapContainerRef,
            mapErrorType,
            recenter,
            registerRecenter,
        }),
        [mapInstance, mapErrorType, recenter, registerRecenter]
    );

    return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

// cSpell:ignore styledata
